import type { LucideIcon } from 'lucide-react'
import { ChatView } from './views/chat-view'
import { ChatQuestionsFloatingBarSlot } from './views/chat-view/question-floating-bar'
import { ChatOutputView } from './views/chat-output-view'
import { ChatOutputFloatingBarSlot } from './views/chat-output-view/batch-floating-controls'
import { MeetingView } from './views/meeting-view'
import { MEETING_VIEW_ID } from './components/meeting/meeting-view-id'
import { MeetingViewFloatingBarSlot } from './views/meeting-view/nested/floating-controls'
import { MeetingViewChatInputBefore } from './views/meeting-view/nested/chat-input-before'
import { ActionView } from './views/action-view'
import { ActionViewFloatingBarSlot } from './views/action-view/nested/floating-controls'
import { HistoryView } from './views/history-view'
import { ActionsView } from './views/actions-view'
import { UpcomingView } from './views/upcoming-view'
import { RemindersView } from './views/reminders-view'
import { ReminderView } from './views/reminder-view'
import { ForYouView } from './views/for-you-view'
import { viewDefinition, type OpenView, type ViewContexts, type ViewId } from './view-definitions'

/** What the floating bar hands a view's control slot: enough to identify the open view. */
export interface ViewSlotProps<TContext> {
  id: string
  context: TContext | undefined
}

/** What each View renders: its component and the chrome it contributes around the composer. */
interface ViewComponents<TContext> {
  render: () => JSX.Element | null
  /**
   * Chrome rendered above the chat input's card (via `ChatInput`'s `before`
   * prop) while this view is on top — for view-owned content that precedes
   * the composer rather than floating over it (the meeting view's audio
   * permissions card). Resolved against the open view's context the same way
   * `floatingBar` is.
   */
  chatInputBefore?: (props: ViewSlotProps<TContext>) => JSX.Element | null
  /** Controls this view contributes to the floating bar above the chat input. */
  floatingBar?: {
    /**
     * How this slot sits among the row's other occupants (jump-to-latest and
     * the shared meeting cluster, which are packed from the leading edge):
     * `stretch` claims everything after them, `compact` sits right-aligned at
     * its natural width.
     *
     * A slot renders controls, not a row. The bar owns the row's spacing.
     */
    layout: 'stretch' | 'compact'
    /** Float jump-to-latest above this slot instead of consuming width beside it. */
    scrollButtonPlacement?: 'inline' | 'above'
    /** Place the live meeting controls below this slot instead of beside it. */
    meetingClusterPlacement?: 'inline' | 'below'
    /** This view shows the live recording's controls itself, so the bar shouldn't add the audio pill. */
    ownsAudioPill?: boolean | ((props: ViewSlotProps<TContext>) => boolean)
    render: (props: ViewSlotProps<TContext>) => JSX.Element | null
  }
}

/**
 * Every View's components, keyed by the ids `view-definitions` declares. A
 * definition without components, or components without a definition, fails to
 * typecheck.
 */
export const viewRegistry: { [K in ViewId]: ViewComponents<ViewContexts[K]> } = {
  chat: {
    render: ChatView,
    floatingBar: {
      layout: 'stretch',
      // Keep jump-to-latest above the full-width question card.
      scrollButtonPlacement: 'above',
      // A question needs the panel's width; keep live meeting controls on the
      // row below instead of squeezing the card beside them.
      meetingClusterPlacement: 'below',
      // Gated on the mirrored flag rather than rendered unconditionally so an
      // idle chat doesn't reserve floating-bar room in the body's bottom inset.
      render: ({ context }) => (context?.hasPendingQuestions ? <ChatQuestionsFloatingBarSlot /> : null),
    },
  },
  chatOutput: {
    render: ChatOutputView,
    floatingBar: {
      layout: 'stretch',
      meetingClusterPlacement: 'below',
      ownsAudioPill: ({ context }) => context?.stagedActionIds?.length === 1,
      render: ({ context }) =>
        context ? (
          <ChatOutputFloatingBarSlot
            actionIds={context.stagedActionIds ?? []}
            conversationId={context.conversationId}
            focusedActionId={context.focusedActionId}
            chatOpen={Boolean(context.chat)}
          />
        ) : null,
    },
  },
  [MEETING_VIEW_ID]: {
    render: MeetingView,
    chatInputBefore: ({ context }) => (context ? <MeetingViewChatInputBefore context={context} /> : null),
    floatingBar: {
      // stretch, not compact: the slot spreads its own two clusters across the
      // row — recording controls (or Delete) at the leading edge, Share at the
      // trailing one — which a right-aligned compact box can't do.
      layout: 'stretch',
      // The slot folds the live-audio pill into its own cluster while recording,
      // or the `tabs` layout's live header carries the controls instead.
      ownsAudioPill: true,
      render: ({ context }) => (context ? <MeetingViewFloatingBarSlot context={context} /> : null),
    },
  },
  action: {
    render: ActionView,
    floatingBar: {
      layout: 'stretch',
      // While a meeting is live the slot puts the pill on the left of its own
      // row and compacts the action controls around it, rather than letting the
      // bar append a third cluster (Figma 8004:32399).
      ownsAudioPill: true,
      render: ({ context }) =>
        context?.actionId ? (
          <ActionViewFloatingBarSlot
            actionId={context.actionId}
            braintrustLink={context.braintrustLink}
            chatOpen={Boolean(context.chat)}
          />
        ) : null,
    },
  },
  history: { render: HistoryView },
  forYou: { render: ForYouView },
  actions: { render: ActionsView },
  upcoming: { render: UpcomingView },
  reminders: { render: RemindersView },
  reminder: { render: ReminderView },
}

/** A registered `floatingBar` as the chrome holds it: `ownsAudioPill` resolved against the open view. */
export type ViewFloatingBar = Omit<NonNullable<ViewComponents<unknown>['floatingBar']>, 'ownsAudioPill'> & {
  ownsAudioPill?: boolean
}

/** What the sidepanel chrome renders for an open view, resolved against its context. */
export interface ViewChrome {
  render: () => JSX.Element | null
  /** Breadcrumb-trail icon. */
  icon: LucideIcon
  /** The view *kind* — "Chat", "Meeting" — for the back/forward tooltips. */
  label: string | undefined
  /** This open document's own name, falling back to `label` for the trail. */
  trailLabel: string | undefined
  /** Composer placeholder, or undefined to keep the default. */
  chatInputPlaceholder: string | undefined
  /** Chrome to render above the chat input's card, or undefined for none. */
  chatInputBefore: ((props: ViewSlotProps<unknown>) => JSX.Element | null) | undefined
  /** Keep the chat input's full-width Home treatment on this view. */
  fullWidthChatInput: boolean
  floatingBar: ViewFloatingBar | undefined
}

// Same widening as `viewDefinition`. Each entry's slots take the context the
// matching definition produced.
const viewComponents = (id: string): ViewComponents<unknown> | undefined =>
  viewRegistry[id as ViewId] as ViewComponents<unknown> | undefined

/**
 * Resolve everything the chrome asks a view for, in one lookup. Undefined for
 * an id outside the registry — Home, which renders outside the router and names
 * itself.
 */
export function viewChrome(view: OpenView): ViewChrome | undefined {
  const definition = viewDefinition(view.id)
  const components = viewComponents(view.id)
  if (!definition || !components) {
    return undefined
  }

  const label = typeof definition.label === 'function' ? definition.label(view.context) : definition.label
  const floatingBar = components.floatingBar
    ? {
        ...components.floatingBar,
        ownsAudioPill:
          typeof components.floatingBar.ownsAudioPill === 'function'
            ? components.floatingBar.ownsAudioPill({ id: view.id, context: view.context })
            : components.floatingBar.ownsAudioPill,
      }
    : undefined
  return {
    render: components.render,
    icon: definition.icon,
    label,
    trailLabel: definition.trailLabel?.(view.context)?.trim() || label,
    chatInputPlaceholder: definition.chatInputPlaceholder,
    chatInputBefore: components.chatInputBefore,
    fullWidthChatInput: definition.fullWidthChatInput,
    floatingBar,
  }
}
